import {
  AddProductToCartError,
  ApiRequest as addProductToCartApiRequest,
} from "Store/addProductToCart/interfaces";
import { ApiErrors, ErrorState } from "Store/interfaces/products";
import {
  UserIdentificationError,
  ApiRequest as userIdentificationApiRequest,
} from "Store/userIdentification/userIdentificationInterfaces";
import Axios, {
  AxiosError,
  AxiosResponse,
  Method,
  RawAxiosRequestHeaders,
  isAxiosError,
} from "axios";
import { call, put } from "redux-saga/effects";
import {
  getCampaignAndLeadQueryParams,
  getMergedQueryParams,
} from "./navigation/queryParameterUtils";

import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { getAccessToken, isAuthenticated } from "./KeycloakWrapper";
import { isDefined } from "./functions";
import { QueryParams } from "./navigation/queryParameterTypes";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const clientId: string = require("../../package.json").name;

// Cache the API root so we don't have to ask
// before every request.
let apiRoot: string | undefined;

const getApiRoot = async (url: string): Promise<string | undefined> => {
  // If the api call starts with /api, we should use the current
  // hostname as the api root
  if (url.includes("/api")) {
    return "";
  }
  // Use cached value
  if (typeof apiRoot !== "undefined") {
    return apiRoot;
  }

  try {
    const { data } = await Axios.get("/api.json");
    apiRoot = data.apihome;
  } catch (e) {
    apiRoot = "https://api.storebrand.no";
  }
  return apiRoot;
};

const createParams = (additionalParams?: QueryParams): any => {
  const urlParams = getCampaignAndLeadQueryParams();
  return getMergedQueryParams([urlParams, additionalParams]);
};

interface RequestType {
  method?: Method;
  requestBody?: any;
  headers?: Record<string, any>;
  params?: QueryParams;
  url: string;
  open?: boolean;
}

export const createRequest = async <R = any>({
  method = "get",
  requestBody = {},
  headers,
  params,
  url,
  open = true,
}: RequestType): Promise<AxiosResponse<R>> =>
  Axios.request<R, AxiosResponse<R>>({
    method,
    url: await constructApiUrl(url),
    data: requestBody,
    headers: await constructHeaders(open, headers),
    params: createParams(params),
    withCredentials: true,
  });

const constructApiUrl = async (url: string): Promise<string> => {
  return `${await getApiRoot(url)}${url}`;
};

export const getError = (error: any): any => {
  if (error.response) {
    return error.response.data;
  }
  if (error.request) {
    return error.request;
  }
  return error;
};

export const getApiError = <T>(
  error: unknown,
  operationType: T,
): ErrorState<T> => {
  if (isAxiosError(error)) {
    const { response }: AxiosError<ApiErrors> = error;

    return {
      status: response?.status,
      response: response?.data,
      operationType,
    };
  }

  return {
    operationType,
  };
};

const constructHeaders = async (
  open: boolean,
  customHeaders?: Record<string, any>,
): Promise<RawAxiosRequestHeaders> => {
  let headers: RawAxiosRequestHeaders = {
    ClientId: clientId,
    ...(customHeaders ?? {}),
  };
  if (!open && isAuthenticated()) {
    const token = await getAccessToken();
    headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return headers;
};

const createRequestData = (
  requestType: Method,
  payload: {
    data: any;
    url: string;
    params?: QueryParams;
    open?: boolean;
  },
): RequestType => {
  const requestData: RequestType = {
    method: requestType,
    url: payload.url,
    params: payload.params,
    open: payload.open,
  };
  if (requestType !== "get") {
    requestData.requestBody = payload.data;
  }
  return requestData;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* request(
  requestType: Method,
  operationFailed: ActionCreatorWithPayload<
    UserIdentificationError | AddProductToCartError,
    string
  >,
  { type, payload }: userIdentificationApiRequest | addProductToCartApiRequest,
  throwNewError = true,
) {
  try {
    const { data } = yield call(
      createRequest,
      createRequestData(requestType, payload),
    );
    return data;
  } catch (e) {
    yield put(
      operationFailed({
        operationType: type,
        response: getError(e),
      }),
    );
    if (throwNewError) {
      throw new Error(`Operation failed for type ${type}`);
    }
  }
  return undefined;
}

export const errorStateHasTechnicalError = (
  error: ErrorState<unknown> | null,
): boolean =>
  isDefined(error) &&
  isDefined(error.status) &&
  isStatusTechnicalError(error.status);

export const isStatusTechnicalError = (status: number): boolean =>
  status >= 500 && status <= 599;

export const hasErrorCode = <T extends string>(
  errorState: ErrorState<unknown> | null,
  errorCode: T,
): boolean =>
  !!errorState?.response?.errors &&
  errorState.response.errors.some((error) => error.errorCode === errorCode);
